import { useEffect, useRef, useState } from 'react';

import { readWithDeadline } from 'lib/readWithDeadline';
import { reportError } from 'lib/reportError';
import {
  AUTH_SESSION_CHANGED_EVENT,
  getAuthSessionRevision,
  isAuthSessionCurrent,
} from 'services/auth/session';
import { AUTH_SESSION_EXPIRED_EVENT } from 'services/events';

interface LookupOwner {
  scope: string;
  revision: number;
  retry: () => void;
}

interface LookupState<T> {
  owner: LookupOwner | null;
  data?: T;
  hasError: boolean;
  isLoading: boolean;
}

// Manual retries only: mutations elsewhere must not reload an in-progress form.
function useAssignmentLookup<T>(
  scope: string | null,
  read: (signal: AbortSignal) => Promise<T>,
  errorContext: string,
): {
  data?: T;
  hasError: boolean;
  isLoading: boolean;
  isReady: boolean;
  retry: () => void;
} {
  const ownerRef = useRef<LookupOwner | null>(null);
  const [state, setState] = useState<LookupState<T>>({
    owner: null,
    hasError: false,
    isLoading: true,
  });

  useEffect(() => {
    if (!scope) return undefined;
    let active = true;
    let currentRequest: AbortController | null = null;
    const owner: LookupOwner = {
      scope,
      revision: getAuthSessionRevision(),
      retry: () => undefined,
    };
    owner.retry = () => {
      if (!active || !isAuthSessionCurrent(owner.revision)) return;
      currentRequest?.abort();
      const request = new AbortController();
      currentRequest = request;
      setState({ owner, hasError: false, isLoading: true });
      const isCurrent = (): boolean =>
        active && currentRequest === request && isAuthSessionCurrent(owner.revision);
      const pending = readWithDeadline(read, request);
      pending
        .then(data => {
          if (isCurrent()) setState({ owner, data, hasError: false, isLoading: false });
        })
        .catch(error => {
          if (isCurrent()) {
            reportError(error, errorContext);
            setState({ owner, hasError: true, isLoading: false });
          }
        })
        .finally(() => request.abort());
    };
    const discard = (): void => {
      active = false;
      currentRequest?.abort();
      if (ownerRef.current === owner) ownerRef.current = null;
    };
    const discardSession = (): void => {
      discard();
      setState({ owner: null, hasError: false, isLoading: true });
    };
    const onStorage = (event: StorageEvent): void => {
      if (event.storageArea && event.storageArea !== localStorage) return;
      if (!event.key || event.key === 'accessToken' || event.key === 'refreshToken')
        discardSession();
    };
    ownerRef.current = owner;
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, discardSession);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, discardSession);
    window.addEventListener('storage', onStorage);
    owner.retry();
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, discardSession);
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, discardSession);
      window.removeEventListener('storage', onStorage);
      discard();
    };
  }, [scope, read, errorContext]);

  const { owner } = state;
  const ownsState =
    owner !== null &&
    owner.scope === scope &&
    ownerRef.current === owner &&
    isAuthSessionCurrent(owner.revision);
  return {
    data: ownsState ? state.data : undefined,
    hasError: Boolean(scope) && ownsState && state.hasError,
    isLoading: Boolean(scope) && (!ownsState || state.isLoading),
    isReady: scope === null || (ownsState && !state.isLoading && !state.hasError),
    retry: () => {
      if (ownsState && ownerRef.current === owner) owner.retry();
    },
  };
}

export { useAssignmentLookup };
